<template>
  <div class="card h-100 overflow-hidden">
    <div class="card-body pt-3 pb-0 pe-0">
      <div class="row me-2">
        <div class="col-12 d-flex justify-content-between">
          <div v-if="period" class="pt-2 text-lighter">
            <i class="fa fa-clock"></i><span class="ms-2 font-weight-bold">{{
              formatPeriod(period)
              }}</span>
          </div>
          <div class="form-group ms-3">
            <PlatoonDropdown id="ddPlatoon" :allow-all="isAdminRole" 
              :selected="selectedPlatoon"
              @selected-platoon-id="(selectedValue) => (selectedPlatoon = selectedValue)" />


            <SectionDropdown style="margin-left: 1rem;" id="ddSectionFilterRecoveryRange" :selected="selectedSection" @selected-section-id="
              (selectedValue) => (selectedSection = selectedValue)
            " />
          </div>
        </div>
        <div class="col-lg-12 col-12">
          <div class="text-xs text-uppercase text-primary text-bold mb-1">
            {{ header }}
          </div>
          <span class="h5 mt-2 text-uppercase">{{
            getSectionName(selectedSection)
            }}</span>

          <div v-if="!hasRangeData" class="text-muted py-3">
            No data found for the selected date range.
          </div>
          <div v-show="hasRangeData" class="mt-4 chart">
            <canvas :id="id" class="chart-canvas" :height="height"></canvas>
          </div>
        </div>

        <div class="col-lg-4 col-12">
          <div class="d-flex flex-column mx-3 position-relative overflow-auto alert-panel mt-sm-4">
            <alert v-for="alert in alerts" :key="alert.message" :color="alert.color" :title="alert.section"
              :body="alert.message" :show-icon="false" />
          </div>
        </div>
        <div class="row bg-dark mt-4">
          <div class="col-12 pt-3">
            <div class="table-responsive">
              <el-table ref="dataTable" :data="dataRecovery.rows" style="width: 100%" @sort-change="sortColumn">
                <el-table-column prop="bucket" label="Date" width="140" sortable="custom">
                  <template #default="scope">
                    {{ formatDate(scope.row.bucket) }}
                  </template>
                </el-table-column>
                <el-table-column prop="name" label="Name" width="320" sortable="custom">
                  <template #default="scope">
                    <span v-if="scope.row.user && scope.row.user.length > 0">
                      {{ scope.row.user[0].name }}
                    </span>
                  </template>
                </el-table-column>
                <el-table-column prop="section_name" label="Section" width="120" sortable="custom">
                  <template #default="scope">
                    <span v-if="scope.row.user && scope.row.user.length > 0">
                      <span class="dot me-2" :style="{
                        'background-color': getSectionColor(
                          scope.row.user[0].section_name
                        ),
                      }"></span>
                      {{ scope.row.user[0].section_name }}
                    </span>
                  </template>
                </el-table-column>
                <el-table-column prop="rhr" label="RHR vs Previous" sortable="custom">
                  <template #default="scope">
                    {{ scope.row.rhr > 0 ? scope.row.rhr : "-" }} bpm
                    <span v-if="scope.row.previous_rhr > 0" class="text-muted">({{ scope.row.previous_rhr }})</span>
                  </template>
                </el-table-column>
                <el-table-column prop="hrv" label="HRV vs Previous" sortable="custom">
                  <template #default="scope">
                    {{ scope.row.hrv > 0 ? scope.row.hrv : "-" }}
                    <span v-if="scope.row.previous_hrv > 0" class="text-muted">({{ scope.row.previous_hrv }})</span>
                  </template>
                </el-table-column>
                <el-table-column prop="recovery_score" label="Recovery Score" sortable="custom">
                  <template #default="scope">
                    <argon-progress :color="getRecoveryColor(scope.row.recovery_score)"
                      :percentage="scope.row.recovery_score" :left-text="`${scope.row.recovery_score}%`" />
                  </template>
                </el-table-column>
                <el-table-column label="" width="60">
                  <template #default="scope">
                    <div class="d-flex">
                      <router-link target="_blank" :to="{
                        name: `User`,
                        params: { udid: scope.row.udid },
                        query: { date: getDate(scope.row.bucket) },
                      }" exact>
                        <icon-user-info />
                      </router-link>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="card-footer paginator">
      <div v-if="dataRecovery && dataRecovery.count > 0" class="d-flex justify-content-between align-items-center">
        <span class="ms-3 text-muted">{{ dataRecovery.count }} record(s)</span>
        <app-pagination :records="dataRecovery.count" :page-size="pageSize" :page="page" @update-page-selection="
          (selectedValue) => updatePageSelection(selectedValue)
        " />
      </div>
    </div>
  </div>
</template>

<script>
const dayjs = require("dayjs");
import Utils from "@/helpers/utils";
import DateUtils from "@/helpers/dateUtils";
import Constants from "@/helpers/constants";
import ChartUtils from "@/helpers/chartUtils";
import Chart from "chart.js/auto";
import annotationPlugin from "chartjs-plugin-annotation";
import Alert from "@/shared/Cards/Alert.vue";
import ArgonProgress from "@/shared/Controls/ArgonProgress.vue";
import IconUserInfo from "@/shared/Controls/Icons/IconUserInfo.vue";
import SectionDropdown from "@/views/pages/components/SectionDropdown.vue";
import PlatoonDropdown from "@/views/pages/components/PlatoonDropdown.vue";

Chart.register(annotationPlugin);

export default {
  name: "RecoveryByDaySectionChart",
  components: {
    ArgonProgress,
    Alert,
    IconUserInfo,
    SectionDropdown,
    PlatoonDropdown,
  },
  mounted() {
    let loggedInUser = this.$store.getters["auth/getLoggedInUser"]();
    if (loggedInUser.role && loggedInUser.role.role_id == Constants.ROLES.PLATOON_COMMANDER) {
      this.selectedPlatoon = loggedInUser.platoon_id;
    }
    if (Constants.ADMIN_USER_ROLES.includes(loggedInUser.role.role_id)) {
       this.isAdminRole = true;
    }
  },
  props: {
    id: {
      type: String,
      default: "recovery-by-day-chart",
    },
    period: {
      type: Array,
      required: true,
    },
    header: {
      type: String,
      default: "Recovery Chart",
    },
    title: {
      type: String,
      default: "Today",
    },
    height: {
      type: [String, Number],
      default: "200",
    },
    selectedSectionId: {
      type: Number,
      default: -1,
    },
    dataRecoveryRanges: {
      type: Array,
      required: true,
    },
    dataRecovery: {
      type: Object,
      required: true,
    },
    chart: {
      type: Object,
      required: true,
      labels: Array,
      datasets: {
        type: Array,
        label: String,
        data: Array,
        color: String,
      },
    },
  },
  emits: [
    "selected-section-id",
    "selected-page",
    "selected-sort",
    "selected-order",
    "selected-platoon-id"
  ],
  data() {
    return {
      page: Constants.DEFAULT_PAGE,
      pageSize: Constants.DEFAULT_PAGE_SIZE,
      selectedSection: Constants.DEFAULT_SECTION,
      sections: [],
      alerts: [],
      annotationsAvg: [],
      selectedPlatoon: Constants.DEFAULT_SECTION,
      isAdminRole: false,
    };
  },
  computed: {
    hasRangeData() {
      return this.dataRecoveryRanges && this.dataRecoveryRanges.length > 0;
    },
  },
  watch: {
    chart() {
      if (this.dataRecoveryRanges && this.dataRecoveryRanges.length > 0)
        this.addChart();
      // this.updateAlerts();
    },
    selectedSection() {
      this.$emit("selected-section-id", this.selectedSection);
    },
    selectedSectionId() {
      this.selectedSection = this.selectedSectionId;
    },
    selectedPlatoon() {
      this.$emit("selected-platoon-id", this.selectedPlatoon);
    },
    selectedPlatoonId() {
      this.selectedPlatoon = this.selectedPlatoonId;
    },
  },
  methods: {
    sortColumn({ prop, order }) {
      this.$emit("selected-sort", prop);
      this.$emit("selected-order", order == "ascending" ? "asc" : "desc");
    },
    getSectionName(sectionId) {
      const section = this.$store.getters["list/getSectionName"](sectionId);
      return section ? section.name : "";
    },
    getSectionColor(sectionName) {
      return Utils.stringToHexColor(sectionName.toUpperCase());
    },
    getRecoveryColor(recoveryScore) {
      return Utils.getRecoveryScoreColor(recoveryScore);
    },
    formatPeriod(period) {
      return Utils.formatPeriod(period);
    },
    formatDate(day) {
      return dayjs(day).format(Constants.DAY_OF_WEEK_TS_FORMAT);
    },
    addChart() {
      this.sections = [];
      this.annotationsAvg = [];

      const ctx = document.getElementById(this.id).getContext("2d");

      let chartStatus = Chart.getChart(this.id);
      if (chartStatus != undefined) {
        chartStatus.destroy();
      }

      let chart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: this.chart.labels,
          datasets: [],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            annotation: {
              annotations: this.annotationsAvg,
            },
            legend: {
              position: "bottom",
              labels: {
                color: "#fff",
                boxWidth: 12,
                boxHeight: 12,
                padding: 40,
              },
            },
          },
          interaction: {
            intersect: false,
            mode: "index",
          },
          scales: {
            x: {
              ticks: {
                color: "#95a7a3",
              },
            },
            y: {
              display: true,
              position: "left",
              min: 0,
              max: 100,
              ticks: {
                stepSize: 10,
                color: "#95a7a3",
              },
              title: {
                display: true,
                text: "RECOVERY SCORE (%)",
                color: "#fff",
                font: {
                  family: "NotoSans-Regular",
                },
              },
              // grid line settings
              grid: {
                drawOnChartArea: true, // only want the grid lines for one axis to show up
              },
            },
          },
        },
      });

      // add dataset
      this.chart.datasets.forEach((data, index) => {
        const randomColor =
          this.selectedSection != -1
            ? ChartUtils.sectionColor(
              this.getSectionName(this.selectedSection).toUpperCase()
            )
            : Utils.stringToHexColor(
              this.getSectionName(index + 1).toUpperCase()
            );

        this.sections.push({
          index: index,
          name: data.label,
          color: randomColor,
        });
        this.annotationsAvg.push({
          id: "avgLine" + index,
          type: "line",
          borderColor: randomColor,
          borderDash: [3, 3],
          borderDashOffset: 0,
          borderWidth: 1,
          label: {
            display: true,
            content: "",
            position: "start",
          },
          scaleID: "y",
          value: this.dataRecoveryRanges[index].average,
        });

        chart.data.datasets.push({
          categoryPercentage: 0.3,
          barPercentage: 1.0,
          label: data.label,
          data: data.data,
          backgroundColor: randomColor,
          tension: 0.4,
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: false,
          maxBarThickness: 8,
        });
      });
      chart.update();
    },
    updateAlerts() {
      if (this.dataRecoveryRanges && this.dataRecoveryRanges.length > 0) {
        this.dataRecoveryRanges.map((record) => {
          record.messages.map((msg) => {
            this.alerts.push({
              section: record.section,
              color: this.getSectionColor(record.section),
              message: msg,
            });
          });
        });
      }
    },
    updatePageSelection(selectedPage) {
      this.page = selectedPage;
      this.$emit("selected-page", selectedPage);
    },
    getDate(date) {
      return DateUtils.formatDateToBucketDate(date)
    },
  },
};
</script>

<style lang="scss" scoped>
.dot {
  height: 8px;
  width: 8px;
  background-color: #fff;
  border-radius: 50%;
  display: inline-block;
}

@media (max-width: 575.98px) {
  .alert-panel {
    height: auto;
    margin-top: 1rem;
  }
}
</style>
